import { type Dispatch } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Grid from '../components/products/grid';
import Pagination from '../components/products/pagination';
import ProductsSkeleton from '../components/products/skeleton';
import StockQuickFilter from '../components/products/stock-quick-filter';
import { type ILanguageState } from '../reducers/language/types';
import {
  type EStockFilter,
  STOCK_FILTER_DEFAULT,
  STOCK_FILTER_IN_STOCK,
  STOCK_FILTER_OUT_OF_STOCK,
} from '../reducers/products/types';
import { queryPage } from '../reducers/search/reducer';
import { type ISearchState } from '../reducers/search/types';
import {
  applyStockToSearchParams,
  parseStockFromSearchParams,
} from '../utils/product-filters-url';

const QUERY_PARAM = 'q';
const PAGE_PARAM = 'page';
const INPUT_DEBOUNCE_MS = 400;
const SEARCH_STOCK_OPTIONS: EStockFilter[] = [
  STOCK_FILTER_IN_STOCK,
  STOCK_FILTER_OUT_OF_STOCK,
];

function SearchContainer({
  dispatch,
  Language,
  Search,
}: {
  dispatch: Dispatch;
  Language: ILanguageState;
  Search: ISearchState;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = (searchParams.get(QUERY_PARAM) ?? '').trim();
  const urlPage = Math.max(1, Number(searchParams.get(PAGE_PARAM)) || 1);
  const urlStock: EStockFilter = parseStockFromSearchParams(searchParams);

  const [inputValue, setInputValue] = useState(urlQuery);

  useEffect((): void => {
    setInputValue(urlQuery);
  }, [urlQuery]);

  useEffect((): void => {
    if (urlQuery.length === 0) {
      return;
    }

    dispatch(
      queryPage({
        query: urlQuery,
        languageType: { code: Language.languageType.code },
        page: urlPage,
        stock: urlStock === STOCK_FILTER_DEFAULT ? undefined : urlStock,
      }),
    );
  }, [urlQuery, urlPage, urlStock, Language.languageType.code, dispatch]);

  const handleStockChange = useCallback(
    (nextStock: EStockFilter): void => {
      setSearchParams((prev: URLSearchParams): URLSearchParams => {
        const next = applyStockToSearchParams(prev, nextStock);
        next.delete(PAGE_PARAM);

        return next;
      });
    },
    [setSearchParams],
  );

  useEffect((): (() => void) => {
    if (inputValue.trim() === urlQuery) {
      return (): void => {};
    }

    const timer = setTimeout((): void => {
      setSearchParams(
        (prev: URLSearchParams): URLSearchParams => {
          const next = new URLSearchParams(prev);
          const trimmed = inputValue.trim();

          if (trimmed.length > 0) {
            next.set(QUERY_PARAM, trimmed);
          } else {
            next.delete(QUERY_PARAM);
          }
          next.delete(PAGE_PARAM);

          return next;
        },
        { replace: true },
      );
    }, INPUT_DEBOUNCE_MS);

    return (): void => clearTimeout(timer);
  }, [inputValue, urlQuery, setSearchParams]);

  const pageTitle: string =
    urlQuery.length > 0
      ? t('search.resultsFor', { query: urlQuery })
      : t('search.title');

  if (typeof document !== 'undefined') {
    document.title = pageTitle;
  }

  const getPageLink = (page: number): string => {
    const next = new URLSearchParams(searchParams);

    if (urlQuery) {
      next.set(QUERY_PARAM, urlQuery);
    }

    if (page > 1) {
      next.set(PAGE_PARAM, String(page));
    } else {
      next.delete(PAGE_PARAM);
    }

    return `?${next.toString()}`;
  };

  const showResults =
    urlQuery.length > 0 &&
    !Search.isLoadingPage &&
    Search.pageProducts.length > 0;
  const showNoResults =
    urlQuery.length > 0 &&
    !Search.isLoadingPage &&
    Search.pageProducts.length === 0;

  return (
    <div className="search-page">
      <div className="container">
        <h1>{pageTitle}</h1>

        <input
          type="search"
          className="search-page__input"
          value={inputValue}
          placeholder={t('layout.searchQuery')}
          aria-label={t('layout.searchQuery')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            setInputValue(e.target.value)
          }
        />

        {urlQuery.length > 0 && (
          <div className="search-page__filters">
            <StockQuickFilter
              value={urlStock}
              availableOptions={SEARCH_STOCK_OPTIONS}
              onChange={handleStockChange}
            />
          </div>
        )}
      </div>

      {urlQuery.length > 0 && Search.isLoadingPage && <ProductsSkeleton />}

      {showNoResults && (
        <div className="container">
          <p role="status">
            {Search.unavailable
              ? t('search.unavailable')
              : t('search.noResults')}
          </p>
        </div>
      )}

      {showResults && (
        <>
          <div className="container">
            <p className="search-page__count">
              {t('products.filter.productCount', { count: Search.total })}
            </p>
          </div>

          <Grid products={Search.pageProducts} headerLevel={2} />

          <Pagination
            page={Search.page}
            pages={Search.pages}
            getPageLink={getPageLink}
          />
        </>
      )}
    </div>
  );
}

const mapStateToProps = ({
  Language,
  Search,
}: {
  Language: ILanguageState;
  Search: ISearchState;
}): { Language: ILanguageState; Search: ISearchState } => ({
  Language,
  Search,
});

export default connect(mapStateToProps)(SearchContainer);
